import {
  AdEnginePlugin,
  type AuctionData,
  type Hooks,
} from "../../core/AdEnginePlugin";
import type { SlotName } from "../../config/platforms/types";
import { A9Wrapper } from "./A9Wrapper";
import { OpenRtbPlugin } from "../OpenRtbPlugin/OpenRtbPlugin";

import {
  BidderEvent,
  type BidResponseInfo,
  PerformanceMarkName,
  PerformanceMeasureName,
  type FandomCmp,
  type AdEngBiddersPayload,
} from "common-types";

import type { OpenRtb2Object } from "../OpenRtbPlugin/openRtb";
import invariant from "tiny-invariant";
import type { A9Bid, A9PluginConfig } from "./a9.d";
import { pushTrack } from "utils";
import type { PageTargeting } from "../../config/page";
import { isConsentGranted } from "../../helpers/isConsentGranted";
import type { AdEngine } from "../../core/AdEngine";

export class A9Plugin extends AdEnginePlugin {
  /**
   * Consistent string representation of the A9 bidder name
   */
  private static readonly A9_BIDDER_NAME = "a9";
  private wrapper: A9Wrapper | null = null;

  /**
   * Checks if the A9 plugin is enabled based on the `icA9Bidder` value in the
   * instant config.
   *
   * @param instantConfig The instant config variables to check
   * @returns True if the plugin should be enabled, false if not.
   */
  static isEnabled(instantConfig: A9PluginConfig): boolean {
    return !!instantConfig.icA9Bidder;
  }

  constructor(private readonly config: A9PluginConfig) {
    super();
  }

  /**
   * Sends data to the tracking plugin to record data about the bids we received
   */
  private trackBidResponse(
    auctionData: AuctionData,
    bids: A9Bid[],
    duration: number,
  ): void {
    bids.forEach((bid) => {
      const slotName = bid.slotID as SlotName;
      let renderCount = 0;
      let baseSlotName = slotName;

      const node = auctionData.adNodes.find(
        (n) => n.getSlotName() === slotName,
      );
      if (node) {
        renderCount = node.getRenderCount();
        baseSlotName = node.getBaseSlotName();
      }

      if (renderCount > 1) {
        // This is a refresh, so don't do anything
        return;
      }

      if (bid.size == "0x0") {
        // From inspecting the bid responses when A9 stopped bidding, this appears to be the indicator This appears to be a "declined to bid" scenario.
        // Context here: https://fandom.atlassian.net/browse/COTECH-2305
        // TODO: an improvement to tracking would be to add the concept of "no_bid" to the adengbidders data; but, this should be handled
        // across the other bidders in Prebid too. Returning early for now.
        return;
      }

      const bidInfo: BidResponseInfo = {
        bidderName: A9Plugin.A9_BIDDER_NAME,
        size: bid.size,
        price: bid.targeting.amznbid,
        timeToRespond: Math.round(duration),
        timedOut: false,
      };

      const bidPayload: AdEngBiddersPayload = {
        slotName,
        baseSlotName,
        rv: renderCount,
        bidInfo: bidInfo,
      };
      pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
    });
  }

  private async executeBidRequest(
    auctionData: AuctionData,
    onCreateBidderAdUnit: NonNullable<Hooks["onCreateBidderAdUnit"]>,
  ): Promise<void> {
    invariant(this.wrapper, "A9Wrapper has not been initialized");

    const startMarkName = `${PerformanceMarkName.AE_AUCTION_START_A9_START}_${auctionData.auctionId}`;
    performance.mark(startMarkName);
    // Update the ortb2 object with the latest signals whenever a bid request is made.
    this.wrapper.updateApstagOrtb2Signals(this.getOrtb2Object());

    const bids = await this.wrapper.requestBids(
      auctionData,
      onCreateBidderAdUnit,
    );

    const { duration } = performance.measure(
      PerformanceMeasureName.AE_AUCTION_DURATION_A9_BID_TOTAL_TIME,
      startMarkName,
    );

    this.trackBidResponse(auctionData, bids, duration);
  }

  private initWrapper(consentData: FandomCmp): void {
    this.wrapper ??= new A9Wrapper(
      consentData,
      this.config.icAdEngineAuctionTimeoutMS,
      this.config,
      this.getOrtb2Object(),
    );
  }

  /**
   * Retrieves the OpenRTB 2.0 object, optionally overriding page targeting.
   *
   * This method uses the `OpenRtbPlugin` to generate an OpenRTB 2.0 object,
   * which contains targeting signals for ad requests. If `pageTargetingOverrides`
   * is provided, it will be used to customize the targeting data.
   *
   * @param {PageTargeting} [pageTargetingOverrides] - Optional overrides for page targeting.
   * @returns {OpenRtb2Object | undefined} The OpenRTB 2.0 object with targeting signals,
   * or `undefined` if the `OpenRtbPlugin` is not available.
   */
  private getOrtb2Object(
    pageTargetingOverrides?: PageTargeting,
  ): OpenRtb2Object | undefined {
    return this.usePlugin(OpenRtbPlugin)?.getOrtb2(pageTargetingOverrides);
  }

  hooks: Hooks = {
    onConsentUpdated: (consentData) => {
      // Do not load A9 at all when COPPA flag is set, as A9 does not provide a COPPA signal
      if (!isConsentGranted(consentData, { ignoreOptOut: true })) {
        return;
      }

      if (this.wrapper) {
        this.wrapper.handleConsentChange(consentData);
      } else {
        this.initWrapper(consentData);
      }
    },
    onCreateBiddersRequests: (
      bidderRequests: Promise<void>[],
      auctionData: AuctionData,
      adEngine: AdEngine,
      onCreateBidderAdUnit: NonNullable<Hooks["onCreateBidderAdUnit"]>,
    ) => {
      if (adEngine.monetizationTierEngine.isFullyMonetized()) {
        bidderRequests.push(
          this.executeBidRequest(auctionData, onCreateBidderAdUnit),
        );
      }
    },
  };
}
